import MatchingChip from 'components/Chips/MatchingChip';
import MentorshipStatusChip from 'components/Chips/MentorshipStatusChip';
import CopyText from 'components/CopyText';
import { ActionDrawer } from 'components/Drawer';
import CheckboxList from 'components/Drawer/CheckboxList';
import EllipsisText from 'components/EllipsisText';
import getFilters from 'components/Filters';
import LabelledCheckbox from 'components/form/Inputs/LabelledCheckbox';
import SettingsIcon from 'components/icons/Settings';
import List from 'components/list';
import {
  ChooseMentor,
  ChooseMentorContainer,
} from 'components/list/ChooseMentor';
import SettingsContainer from 'components/list/SettingsContainer';
import { BoldTd, Td } from 'components/list/Td';
import { getOrderTh, Th } from 'components/list/Th';
import { getColumnNumber } from 'components/list/utils';
import { DefaultLoader, LoaderContainer } from 'components/Loader';
import MENTORSHIP_STATUS from 'enums/MentorshipStatus';
import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  setActiveColumns,
  setOrder,
  setPage,
  setSearch,
  setTags,
  setTake,
} from 'store/actions/admin/mentorLists';
import { StoreState } from 'store/reducers';
import useSWR from 'swr';
import GetOrderValue from 'types/GetOrderValue';
import Mentorship from 'types/Mentorship';
import Tags from 'types/Tags';
import { Action } from 'types/typeguards/reducerActions';
import User from 'types/User';
import { historyTags } from 'utils/mentorshipTags';
import { getLocaleDate } from 'utils/time';
import { MentorEditContext } from '.';

export const columnNames = [
  'matching',
  'firstName',
  'lastName',
  'email',
  'region',
  'start',
  'end',
  'status',
] as const;
export type ColumnNames = typeof columnNames[number];
export type Columns = {
  [key in ColumnNames]: boolean;
};

export const sortableColumns = [
  'matching',
  'firstName',
  'lastName',
  'start',
  'end',
  'status',
] as const;
export type OrderValue = GetOrderValue<typeof sortableColumns[number]>;
const OrderTh = getOrderTh<typeof sortableColumns[number]>();

export const initialActiveColumns: Columns = {
  matching: true,
  firstName: true,
  lastName: true,
  email: true,
  region: false,
  start: true,
  end: true,
  status: true,
};

export const tagColumnNames = ['status'] as const;
export type TagColumnNames = typeof tagColumnNames[number];

type RetrievedData = {
  mentorships: Mentorship[] | undefined;
  count: number | undefined;
  pages: number | undefined;
};

type Props = {
  page: number;
  setPage: (page: number) => void;
  search: string;
  setSearch: (search: string) => void;
  tags: Tags<TagColumnNames>;
  setTags: (tag: Tags<TagColumnNames>) => void;
  activeColumns: Columns;
  setActiveColumns: (activeColumns: Columns) => void;
  take: number;
  setTake: (take: number) => void;
  order: OrderValue;
  setOrder: (order: OrderValue) => void;
};

type ContextType = {
  mentor: User | undefined;
  mentorships: Mentorship[] | undefined;
  pages: number | undefined;
  loading: boolean;
} & Props;
const HistoryContext = React.createContext({} as ContextType);
const { Provider } = HistoryContext;

const useCustomSWR = (
  id: number | undefined,
  take: number,
  page: number,
  search: string,
  tags: Tags<TagColumnNames>,
  order: OrderValue,
) => {
  const { data, error } = useSWR(
    `/mentorship/mentor/${id}/history?take=${take}&page=${page}\
		${search ? `&search=${search}` : ''}\
		${
      tags.status[0]
        ? tags.status.map((tag) => `&mentorshipTags=${tag}`).join('')
        : ''
    }&order=${order}`,
  );
  if (!id) {
    return { data: undefined };
  }
  return { data, error };
};

const History: FC<Props> = ({
  children,
  page,
  setPage,
  search,
  setSearch,
  tags,
  setTags,
  activeColumns,
  setActiveColumns,
  take,
  setTake,
  order,
  setOrder,
}) => {
  const { mentor } = useContext(MentorEditContext);

  const { data, error }: { data?: RetrievedData; error?: unknown } =
    useCustomSWR(mentor?.id, take, page, search, tags, order);

  const [retrievedData, setRetrievedData] = useState<RetrievedData>({
    mentorships: undefined,
    count: undefined,
    pages: undefined,
  });
  useEffect(() => {
    if (data) {
      setRetrievedData(data);
    }
  }, [data]);

  // Reset page to 0 if the stored page is out of range
  useEffect(() => {
    if (data?.pages && data?.pages <= page) {
      setPage(0);
    }
  }, [data, page, setPage]);

  const value = useMemo(
    () => ({
      mentor,
      mentorships: retrievedData.mentorships,
      pages: retrievedData.pages,
      page,
      setPage,
      search,
      setSearch,
      tags,
      setTags,
      activeColumns,
      setActiveColumns,
      loading: !data,
      take,
      setTake,
      order,
      setOrder,
    }),
    [
      mentor,
      search,
      setSearch,
      tags,
      setTags,
      activeColumns,
      setActiveColumns,
      page,
      setPage,
      data,
      retrievedData,
      take,
      setTake,
      order,
      setOrder,
    ],
  );

  return error ? (
    <p>L'utilisateur n'a pas rempli son formulaire correctement.</p>
  ) : retrievedData.mentorships ? (
    <Provider value={value}>
      <section>{children}</section>
    </Provider>
  ) : (
    <LoaderContainer height="auto">
      <DefaultLoader />
    </LoaderContainer>
  );
};

const Filters = getFilters<TagColumnNames>();
const HistoryFilter: FC = () => {
  const { search, setSearch, tags, setTags, take, setTake } =
    useContext(HistoryContext);

  return (
    <Filters
      tagList={[
        {
          columnName: 'status',
          columnTitle: 'Statut du mentorat',
          tags: historyTags.map((tag) => {
            return {
              name: tag,
              label: MENTORSHIP_STATUS[tag],
              labelComponent: <MentorshipStatusChip status={tag} />,
            };
          }),
        },
      ]}
      state={{ tags, search, take }}
      setTags={setTags}
      setSearch={setSearch}
      setTake={setTake}
    />
  );
};

const HistoryList: FC = () => {
  const {
    mentor,
    mentorships,
    pages,
    page,
    setPage,
    loading,
    activeColumns,
    setActiveColumns,
    order,
    setOrder,
  } = useContext(HistoryContext);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const [selectedColumns, setSelectedColumns] = useState(activeColumns);

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setSelectedColumns({
      ...selectedColumns,
      [e.target.name]: e.target.checked,
    });
  };

  const columnNumber = useMemo(() => {
    return getColumnNumber(activeColumns, false);
  }, [activeColumns]);

  return (
    <>
      <ActionDrawer
        open={settingsOpen}
        close={() => setSettingsOpen(false)}
        title="Colonnes mentorés"
        subtitle="Activer les colonnes que vous souhaitez"
        cancel={() => {
          setSelectedColumns(activeColumns);
        }}
        apply={() => {
          setActiveColumns(selectedColumns);
        }}
      >
        <CheckboxList>
          <LabelledCheckbox
            label="Matching"
            checked={selectedColumns.matching}
            name="matching"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Email"
            checked={selectedColumns.email}
            name="email"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Région"
            checked={selectedColumns.region}
            name="region"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Date de début"
            checked={selectedColumns.start}
            name="start"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Date de fin"
            checked={selectedColumns.end}
            name="end"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Statut du mentorat"
            checked={selectedColumns.status}
            name="status"
            onChange={handleChange}
          />
        </CheckboxList>
      </ActionDrawer>
      <List
        activeColumns={activeColumns}
        columnNumber={columnNumber}
        isEmpty={mentorships?.length === 0}
        emptyText="L'historique de ce mentor est vide pour cette recherche"
        pages={pages}
        page={page}
        setPage={setPage}
        loading={loading}
      >
        <OrderTh name="matching" order={order} setOrder={setOrder}>
          Matching
        </OrderTh>
        <OrderTh name="firstName" order={order} setOrder={setOrder}>
          Prénom
        </OrderTh>
        <OrderTh name="lastName" order={order} setOrder={setOrder}>
          Nom
        </OrderTh>
        <Th name="email">Email</Th>
        <Th name="region">Région</Th>
        <OrderTh name="start" order={order} setOrder={setOrder}>
          Date début
        </OrderTh>
        <OrderTh name="end" order={order} setOrder={setOrder}>
          Date de fin
        </OrderTh>
        <OrderTh name="status" order={order} setOrder={setOrder}>
          Statut du mentorat
        </OrderTh>
        <Th name="settings">
          <SettingsContainer onClick={() => setSettingsOpen(true)}>
            <SettingsIcon />
          </SettingsContainer>{' '}
        </Th>
        {mentorships?.map((mentorship, idx) => (
          <React.Fragment key={idx}>
            <Td name="matching">
              {mentorship.mentee?.MenteeMatching &&
                mentorship.mentee.MenteeMatching[0] && (
                  <MatchingChip
                    value={mentorship.mentee.MenteeMatching[0].value}
                  />
                )}
            </Td>
            <BoldTd name="firstName">
              <Link
                to={`/admin/mentoring/mentees/${mentorship.mentee?.id}/mentoring/`}
              >
                <EllipsisText text={mentorship.mentee?.firstName || ''} />
              </Link>
            </BoldTd>
            <BoldTd name="lastName">
              <Link
                to={`/admin/mentoring/mentees/${mentorship.mentee?.id}/mentoring/`}
              >
                <EllipsisText text={mentorship.mentee?.lastName || ''} />
              </Link>
            </BoldTd>
            <Td name="email">
              <EllipsisText text={mentorship.mentee?.email || ''} />
              <CopyText text="" copiedText={mentorship.mentee?.email || ''} />
            </Td>
            <Td name="region">
              <EllipsisText text={mentorship.mentee?.region || ''} />
            </Td>
            <Td name="start">
              {mentorship?.createdAt && getLocaleDate(mentorship.createdAt)}
            </Td>
            <Td name="end">
              {mentorship?.updatedAt && getLocaleDate(mentorship.updatedAt)}
            </Td>
            <Td name="status">
              <MentorshipStatusChip
                status={mentorship.status || 'FINISHED_DATE'}
              />
            </Td>
            <Td name="settings">
              <ChooseMentorContainer>
                <ChooseMentor
                  menteeId={mentorship.mentee?.id || -1}
                  mentorId={mentor?.id || -1}
                  redirect={`/admin/mentoring/mentors/${
                    mentor!.id
                  }/mentoring/ongoing`}
                >
                  Selectionner comme mentor
                </ChooseMentor>
              </ChooseMentorContainer>
            </Td>
          </React.Fragment>
        ))}
      </List>
    </>
  );
};

const Usage: FC<Props> = (props) => {
  return (
    <History {...props}>
      <HistoryFilter />
      <HistoryList />
    </History>
  );
};

const mapStateToProps = (state: StoreState) => {
  return {
    page: state.admin.mentorLists.history.page,
    search: state.admin.mentorLists.history.search,
    tags: state.admin.mentorLists.history.tags,
    activeColumns: state.admin.mentorLists.history.activeColumns,
    take: state.admin.mentorLists.history.take,
    order: state.admin.mentorLists.history.order,
  };
};

const mapDispatchToProps = (dispatch: (action: Action) => void) => {
  return {
    setPage: (page: number) => dispatch(setPage('history', page)),
    setSearch: (search: string) => dispatch(setSearch('history', search)),
    setTags: (tags: Tags<TagColumnNames>) => dispatch(setTags('history', tags)),
    setActiveColumns: (activeColumns: Columns) =>
      dispatch(setActiveColumns<Columns>('history', activeColumns)),
    setTake: (take: number) => dispatch(setTake('history', take)),
    setOrder: (order: OrderValue) => dispatch(setOrder('history', order)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Usage);
